import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Badge } from '../components/ui/badge';
import SlateLogo from '../components/SlateLogo';
import {
  Users, DollarSign, Clock, CheckCircle2, Circle, Loader2,
  FileText, Film, Image, Archive, Music, FolderOpen, ArrowLeft
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.06 } } };

const statusIcon = (s) => {
  if (s === 'done') return <CheckCircle2 className="w-3.5 h-3.5 text-green-600" strokeWidth={2} />;
  if (s === 'in-progress') return <Loader2 className="w-3.5 h-3.5 text-amber-500 animate-spin" strokeWidth={2} />;
  return <Circle className="w-3.5 h-3.5 text-muted-foreground/50" strokeWidth={2} />;
};

const typeIcon = { document: FileText, video: Film, design: Image, archive: Archive, audio: Music, spreadsheet: FileText };

export default function SharedProjectPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/shared/${token}`)
      .then(r => { if (!r.ok) throw new Error('not found'); return r.json(); })
      .then(setData)
      .catch(() => setError('This project link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4" data-testid="shared-error">
      <SlateLogo size={32} />
      <p className="text-lg font-medium">Link not found</p>
      <p className="text-sm text-muted-foreground">{error}</p>
      <Link to="/" className="text-sm text-[hsl(36,80%,52%)] hover:underline">Go to Slate</Link>
    </div>
  );

  const { project, team, tasks, files } = data;
  const doneTasks = tasks.filter(t => t.status === 'done').length;
  const budget = project.budget || 0;

  return (
    <div className="min-h-screen bg-background" data-testid="shared-project-page">
      {/* Nav bar */}
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <SlateLogo size={18} />
            <span className="font-heading text-sm font-semibold">Slate</span>
          </Link>
          <Badge variant="secondary" className="text-[10px] h-5 px-2">Read-only</Badge>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          {/* Header */}
          <motion.div variants={fadeUp} className="mb-8">
            <Badge variant="secondary" className="text-[10px] h-5 px-2 mb-3 capitalize">{project.project_type || 'Project'}</Badge>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">{project.name}</h1>
            {project.description && <p className="text-muted-foreground mt-2 max-w-2xl">{project.description}</p>}
          </motion.div>

          {/* Stats */}
          <motion.div variants={fadeUp} className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
            <div className="p-4 rounded-xl border border-border/50 bg-card">
              <Users className="w-4 h-4 text-muted-foreground mb-2" />
              <p className="font-heading text-xl font-semibold">{team.length}</p>
              <p className="text-[10px] text-muted-foreground">Team Members</p>
            </div>
            <div className="p-4 rounded-xl border border-border/50 bg-card">
              <CheckCircle2 className="w-4 h-4 text-muted-foreground mb-2" />
              <p className="font-heading text-xl font-semibold">{doneTasks}/{tasks.length}</p>
              <p className="text-[10px] text-muted-foreground">Tasks Done</p>
            </div>
            <div className="p-4 rounded-xl border border-border/50 bg-card">
              <DollarSign className="w-4 h-4 text-muted-foreground mb-2" />
              <p className="font-heading text-xl font-semibold">{budget > 0 ? `₹${(budget / 100000).toFixed(1)}L` : '—'}</p>
              <p className="text-[10px] text-muted-foreground">Budget</p>
            </div>
            <div className="p-4 rounded-xl border border-border/50 bg-card">
              <FolderOpen className="w-4 h-4 text-muted-foreground mb-2" />
              <p className="font-heading text-xl font-semibold">{files.length}</p>
              <p className="text-[10px] text-muted-foreground">Files</p>
            </div>
          </motion.div>

          {/* Team */}
          {team.length > 0 && (
            <motion.div variants={fadeUp} className="mb-10">
              <h2 className="font-heading text-lg font-semibold mb-4">Team</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {team.map((m, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-card" data-testid={`shared-team-${i}`}>
                    {m.avatar ? (
                      <img src={m.avatar} alt={m.name} className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-xs font-medium">{m.name?.[0]}</div>
                    )}
                    <div>
                      <p className="text-sm font-medium">{m.name}</p>
                      <p className="text-[10px] text-muted-foreground">{m.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Tasks */}
          {tasks.length > 0 && (
            <motion.div variants={fadeUp} className="mb-10">
              <h2 className="font-heading text-lg font-semibold mb-4">Tasks</h2>
              <div className="space-y-2">
                {tasks.map((t, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-card" data-testid={`shared-task-${i}`}>
                    {statusIcon(t.status)}
                    <span className="text-sm flex-1">{t.title}</span>
                    {t.assignee && <span className="text-[10px] text-muted-foreground">{t.assignee}</span>}
                    <Badge variant="secondary" className="text-[9px] h-4 px-1 capitalize">{t.status}</Badge>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Files */}
          {files.length > 0 && (
            <motion.div variants={fadeUp}>
              <h2 className="font-heading text-lg font-semibold mb-4">Files</h2>
              <div className="space-y-2">
                {files.map((f, i) => {
                  const Icon = typeIcon[f.file_type] || FileText;
                  return (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-card" data-testid={`shared-file-${i}`}>
                      <div className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center">
                        <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={2} />
                      </div>
                      <span className="text-sm flex-1">{f.name}</span>
                      <span className="text-[10px] text-muted-foreground">{f.size}</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
